import React from "react";
import { FileText, ExternalLink, Shield, Heart, Lock, ClipboardList } from "lucide-react";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import HeroSection from "@/components/HeroSection";
import Section from "@/components/Section";
import SectionHeader from "@/components/SectionHeader";
import { Button } from "@/components/Button";

// ============================================
// IMMAGINI
// ============================================

const HERO_IMAGE = "/images/99-castelli-header.webp";

// ============================================
// DOCUMENTI CSI
// ============================================

const documenti = [
  {
    title: "Norme di tesseramento CSI",
    description: "Regolamento completo per il tesseramento al Centro Sportivo Italiano, requisiti e procedure.",
    url: "https://www.centrosportivoitaliano.it/uploads/Norme%20di%20tesseramento%202023-2024_web.pdf",
    icon: ClipboardList,
    color: "bg-blue-500",
  },
  {
    title: "Polizza assicurativa CSI",
    description: "Dettagli sulla copertura assicurativa inclusa nel tesseramento e opzioni aggiuntive disponibili.",
    url: "https://www.centrosportivoitaliano.it/servizi/assicurazione/assicurazioni",
    icon: Shield,
    color: "bg-green-500",
  },
  {
    title: "Tutela sanitaria CSI",
    description: "Informazioni sulla tutela sanitaria delle attività sportive e certificati medici richiesti.",
    url: "https://www.centrosportivoitaliano.it/servizi/tutela-sanitaria/tutela-sanitaria-delle-attivita-sportive",
    icon: Heart,
    color: "bg-red-500",
  },
  {
    title: "Privacy Policy CSI",
    description: "Informativa sulla privacy e trattamento dei dati personali secondo il GDPR.",
    url: "https://www.centrosportivoitaliano.it/p/privacy-policy",
    icon: Lock,
    color: "bg-purple-500",
  },
];

// ============================================
// COMPONENTE PRINCIPALE
// ============================================

const Documenti: React.FC = () => {
  return (
    <PageLayout>
      {/* Hero Section */}
      <HeroSection
        backgroundImage={HERO_IMAGE}
        title="Documenti"
        subtitle="Regolamenti, polizze e normative del CSI - Centro Sportivo Italiano"
        height="medium"
      />

      {/* Intro Section */}
      <Section background="cream">
        <div className="max-w-4xl mx-auto text-center">
          <SectionHeader title="Documentazione CSI" subtitle="Tutto ciò che devi sapere" />
          <p className="text-text-light text-lg">
            In questa pagina troverai tutti i collegamenti al sito del{" "}
            <strong className="text-primary">CSI - Centro Sportivo Italiano</strong>, l'ente di promozione sportiva con
            cui ASD 99 Castelli è affiliata. Consulta i documenti per conoscere le norme di tesseramento, la copertura
            assicurativa, la tutela sanitaria e la privacy policy.
          </p>
        </div>
      </Section>

      {/* Documenti Cards */}
      <Section background="white">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {documenti.map((doc, index) => {
              const IconComponent = doc.icon;
              return (
                <motion.div
                  key={doc.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white border border-gray-200 rounded-xl shadow-elegant hover:shadow-elegant-lg transition-all duration-300 overflow-hidden group"
                >
                  <div className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`${doc.color} p-3 rounded-lg text-white flex-shrink-0`}>
                        <IconComponent size={24} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-heading font-bold text-primary mb-2 group-hover:text-primary-light transition-colors">
                          {doc.title}
                        </h3>
                        <p className="text-text-light text-sm mb-4">{doc.description}</p>
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition-colors"
                        >
                          <span>APRI DOCUMENTO</span>
                          <ExternalLink size={16} />
                        </a>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* Info CSI */}
      <Section background="cream">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl p-8 shadow-elegant">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <img
                src="/images/logo-csi.png"
                alt="CSI - Centro Sportivo Italiano"
                className="h-24 object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
              <div className="text-center md:text-left">
                <h3 className="text-2xl font-heading font-bold text-primary mb-2">CSI - Centro Sportivo Italiano</h3>
                <p className="text-text-light mb-4">
                  Il CSI è un ente di promozione sportiva riconosciuto dal CONI che promuove lo sport come momento di
                  educazione, crescita, impegno e aggregazione sociale. ASD 99 Castelli è orgogliosamente affiliata al
                  CSI per garantire ai propri tesserati copertura assicurativa e tutela sanitaria.
                </p>
                <a
                  href="https://www.centrosportivoitaliano.it"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-primary font-semibold hover:text-primary-light transition-colors"
                >
                  <span>Visita il sito CSI</span>
                  <ExternalLink size={16} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* CTA Section */}
      <Section background="primary">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">Pronto per Tesserarti?</h2>
          <p className="text-white/90 text-lg mb-8">
            Scopri tutte le tipologie di tesseramento disponibili e unisciti alla nostra comunità.
          </p>
          <Button href="/tesseramento" variant="secondary" size="lg" icon={<FileText size={20} />}>
            Vai al Tesseramento
          </Button>
        </div>
      </Section>
    </PageLayout>
  );
};

export default Documenti;
